/* Predictive Analytics per subject — iter75.
 * At-risk students dashboard for admin/director/teacher_user.
 * Route: /predictive-analytics
 */
import { useEffect, useState, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Brain, Warning, TrendUp, TrendDown, ArrowClockwise, GraduationCap, ChartLine,
  Lifebuoy as LifeBuoy,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from 'recharts';
import api from '../lib/api';

const RISK_META = {
  critical: { label: 'Critique', color: '#B91C1C', bg: '#FEE2E2', icon: '🔴' },
  at_risk: { label: 'À risque', color: '#DC2626', bg: '#FEF2F2', icon: '🟠' },
  warning: { label: 'Vigilance', color: '#B45309', bg: '#FEF3C7', icon: '🟡' },
  safe: { label: 'Sécurisé', color: '#059669', bg: '#ECFDF5', icon: '🟢' },
  unknown: { label: 'Inconnu', color: '#64748B', bg: '#F1F5F9', icon: '⚪' },
};

export default function PredictiveAnalytics() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState('');
  const [schoolYear, setSchoolYear] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [c, y] = await Promise.all([
          api.get('/classes').then((r) => r.data),
          api.get('/school-years/active').then((r) => r.data),
        ]);
        setClasses(c);
        setSchoolYear(y?.name || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`);
        if (c.length) setClassName(c[0].name);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!className || !schoolYear) return;
    setLoading(true);
    try {
      const { data } = await api.get('/grades/predictive-analytics', {
        params: { class_name: className, school_year: schoolYear },
      });
      setData(data);
      setExpanded(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [className, schoolYear]);

  useEffect(() => { load(); }, [load]);

  const toggle = (sid) => {
    const s = new Set(expanded);
    if (s.has(sid)) s.delete(sid); else s.add(sid);
    setExpanded(s);
  };

  const [generatingPlans, setGeneratingPlans] = useState(false);
  const generatePlans = async () => {
    if (!className || !schoolYear) return;
    setGeneratingPlans(true);
    try {
      const { data: r } = await api.post('/support-plans/auto-generate', {
        class_name: className, school_year: schoolYear,
      });
      toast.success(
        `${r.created} plan(s) créé(s), ${r.updated} mis à jour — Redirection vers /support-plans`,
      );
      setTimeout(() => navigate('/support-plans'), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération');
    } finally { setGeneratingPlans(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="predictive-analytics-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
            <Brain size={28} weight="duotone" className="text-[#0052FF]" />
            Analytics prédictifs — Élèves à risque
          </h1>
          <p className="text-sm text-[#64748B]">
            Analyse par matière basée sur la moyenne, la tendance (régression linéaire) et
            la pire note. Identifie les élèves nécessitant un accompagnement pédagogique.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Classe</Label>
            <Select value={className} onValueChange={setClassName}>
              <SelectTrigger className="w-48 h-9" data-testid="pa-class-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id || c.name} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Année scolaire</Label>
            <Input
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              className="w-32 h-9"
              data-testid="pa-year-input"
            />
          </div>
          <Button onClick={load} variant="outline" data-testid="pa-refresh">
            <ArrowClockwise size={14} className="mr-1" />
            {loading ? '…' : 'Actualiser'}
          </Button>
          <Button onClick={generatePlans} disabled={generatingPlans}
            data-testid="pa-generate-plans"
            className="bg-[#0052FF] hover:bg-[#0044CC]">
            <LifeBuoy size={14} className="mr-1" />
            {generatingPlans ? '…' : 'Générer plans de soutien'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-12 text-[#94A3B8]">Chargement…</p>
      ) : !data ? (
        <p className="text-center py-12 text-[#94A3B8]">
          Sélectionnez une classe pour afficher l'analyse.
        </p>
      ) : data.kpi.total_students === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <GraduationCap size={48} className="mx-auto text-[#CBD5E1] mb-3" weight="duotone" />
            <p className="text-sm text-[#64748B]">
              Aucune note enregistrée pour cette classe. Ajoutez des notes pour activer
              l'analyse prédictive.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-[10px] uppercase text-[#64748B] font-bold">Élèves analysés</p>
                <p className="text-3xl font-outfit font-bold" data-testid="pa-kpi-total">
                  {data.kpi.total_students}
                </p>
                <p className="text-[10px] text-[#94A3B8]">moyenne classe : {data.kpi.overall_mean}/20</p>
              </CardContent>
            </Card>
            {['critical', 'at_risk', 'warning', 'safe'].map((level) => (
              <Card key={level}>
                <CardContent className="p-4"
                  style={{ background: RISK_META[level].bg }}>
                  <p className="text-[10px] uppercase font-bold"
                    style={{ color: RISK_META[level].color }}>
                    {RISK_META[level].icon} {RISK_META[level].label}
                  </p>
                  <p className="text-3xl font-outfit font-bold"
                    style={{ color: RISK_META[level].color }}
                    data-testid={`pa-kpi-${level}`}>
                    {data.kpi[level]}
                  </p>
                  <p className="text-[10px]"
                    style={{ color: RISK_META[level].color + 'AA' }}>
                    {data.kpi.total_students
                      ? Math.round(100 * data.kpi[level] / data.kpi.total_students)
                      : 0}%
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Subject risk chart */}
          {data.subject_risk.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-outfit font-semibold mb-2 flex items-center gap-2">
                  <ChartLine size={18} weight="duotone" className="text-[#0052FF]" />
                  Répartition du risque par matière
                </h3>
                <div style={{ width: '100%', height: 300, minWidth: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.subject_risk}
                      margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="subject" tick={{ fontSize: 11 }}
                        angle={-15} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="critical" stackId="a" fill={RISK_META.critical.color}
                        name="Critique" />
                      <Bar dataKey="at_risk" stackId="a" fill={RISK_META.at_risk.color}
                        name="À risque" />
                      <Bar dataKey="warning" stackId="a" fill={RISK_META.warning.color}
                        name="Vigilance" />
                      <Bar dataKey="safe" stackId="a" fill={RISK_META.safe.color}
                        name="Sécurisé" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Students list */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-3 flex items-center gap-2">
                <Warning size={18} weight="duotone" className="text-amber-600" />
                Élèves classés par risque
              </h3>
              <table className="w-full text-sm" data-testid="pa-students-table">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Élève</th>
                    <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Risque</th>
                    <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Moyenne</th>
                    <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Matières critiques</th>
                    <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Matières à risque</th>
                    <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s) => {
                    const meta = RISK_META[s.risk_level] || RISK_META.unknown;
                    const isExpanded = expanded.has(s.student_id);
                    return (
                      <Fragment key={s.student_id}>
                        <tr
                          className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]"
                          data-testid={`pa-row-${s.student_id}`}
                          data-risk={s.risk_level}>
                          <td className="py-2 px-2 font-medium">{s.student_name}</td>
                          <td className="py-2 px-2 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                              style={{ background: meta.bg, color: meta.color }}>
                              {meta.icon} {meta.label}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right font-semibold"
                            style={{
                              color: s.overall_mean == null ? '#94A3B8'
                                : s.overall_mean < 10 ? '#DC2626'
                                : s.overall_mean < 12 ? '#F59E0B'
                                : '#059669',
                            }}>
                            {s.overall_mean != null ? `${s.overall_mean}/20` : '—'}
                          </td>
                          <td className="py-2 px-2 text-center text-red-700 font-bold">
                            {s.critical_subjects}
                          </td>
                          <td className="py-2 px-2 text-center text-amber-700 font-bold">
                            {s.at_risk_subjects}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <button
                              type="button" onClick={() => toggle(s.student_id)}
                              className="text-xs text-[#0052FF] hover:underline"
                              data-testid={`pa-toggle-${s.student_id}`}
                            >
                              {isExpanded ? 'Masquer' : 'Voir'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-[#F8FAFC]">
                            <td colSpan="6" className="p-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {s.subjects.map((subj) => {
                                  const sm = RISK_META[subj.risk_level] || RISK_META.unknown;
                                  return (
                                    <div key={subj.subject}
                                      className="p-2 rounded border border-[#E2E8F0] bg-white"
                                      data-testid={`pa-subject-${s.student_id}-${subj.subject}`}>
                                      <div className="flex justify-between text-xs mb-1">
                                        <span className="font-semibold">{subj.subject}</span>
                                        <span className="font-bold" style={{ color: sm.color }}>
                                          {sm.icon} {sm.label}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-4 gap-2 text-[10px] text-[#64748B]">
                                        <span>Notes : <b className="text-[#0F172A]">{subj.count}</b></span>
                                        <span>Moy. : <b className="text-[#0F172A]">{subj.mean}/20</b></span>
                                        <span>Min : <b className="text-red-600">{subj.min}</b></span>
                                        <span>Max : <b className="text-emerald-600">{subj.max}</b></span>
                                      </div>
                                      {subj.slope != null && (
                                        <div className="text-[10px] text-[#64748B] mt-1 flex items-center gap-1">
                                          Tendance : {subj.slope > 0
                                            ? <><TrendUp size={11} className="text-emerald-600" /> +{subj.slope}</>
                                            : subj.slope < 0
                                              ? <><TrendDown size={11} className="text-red-600" /> {subj.slope}</>
                                              : <>stable</>}
                                          {subj.predicted_next != null && (
                                            <>
                                              &nbsp;•&nbsp;Prévision : <b className="text-[#0052FF]">{subj.predicted_next}/20</b>
                                            </>
                                          )}
                                        </div>
                                      )}
                                      {subj.count >= 2 && (
                                        <div style={{ height: 60, marginTop: 4 }}>
                                          <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={subj.notes.map((n) => ({
                                              period: n.period_key.split('-').pop(),
                                              note: n.value_20,
                                            }))} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                                              <XAxis dataKey="period" tick={{ fontSize: 8 }} />
                                              <YAxis domain={[0, 20]} tick={{ fontSize: 8 }} width={20} />
                                              <Tooltip />
                                              <Line type="monotone" dataKey="note"
                                                stroke={sm.color} strokeWidth={2} dot={{ r: 2 }} />
                                            </LineChart>
                                          </ResponsiveContainer>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
